<template>
  <main v-if="user" class="flex justify-center min-h-screen bg-gray-100">
    <HeaderComponent />
    <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px]">
      <!-- Messages d'alerte -->
      <div class="fixed top-[80px] left-[50%] lg:left-[calc(50%+140px)] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
        <div v-if="errorMessage" class="text-sm w-full rounded-lg shadow-lg text-white bg-red-600 my-[10px] font-bold text-center p-4">
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="text-sm w-full rounded-lg shadow-lg text-white bg-green-600 my-[10px] font-bold text-center p-4">
          {{ successMessage }}
        </div>
      </div>

      <!-- Filtres rapides -->
      <div v-if="user.isSuper && isfiltre" class="flex flex-wrap gap-2 mb-4">
        <button @click="clearStockFilter" :class="!selectedStock ? 'bg-orange-500 text-white' : 'bg-white text-gray-800 border border-orange-500'" class="px-3 py-2 rounded-lg text-sm font-medium transition-colors">
          Tous les stocks
        </button>
        <button @click="filterByStock('Stock Calavi')" :class="selectedStock === 'Stock Calavi' ? 'bg-orange-500 text-white' : 'bg-white text-gray-800 border border-orange-500'" class="px-3 py-2 rounded-lg text-sm font-medium transition-colors">
          Stock Calavi
        </button>
        <button @click="filterByStock('Stock Parakou')" :class="selectedStock === 'Stock Parakou' ? 'bg-orange-500 text-white' : 'bg-white text-gray-800 border border-orange-500'" class="px-3 py-2 rounded-lg text-sm font-medium transition-colors">
          Stock Parakou
        </button>
        <button @click="filterByStock('Magasin Calavi')" :class="selectedStock === 'Magasin Calavi' ? 'bg-orange-500 text-white' : 'bg-white text-gray-800 border border-orange-500'" class="px-3 py-2 rounded-lg text-sm font-medium transition-colors">
          Magasin Calavi
        </button>
        <button @click="filterByStock('Magasin Parakou')" :class="selectedStock === 'Magasin Parakou' ? 'bg-orange-500 text-white' : 'bg-white text-gray-800 border border-orange-500'" class="px-3 py-2 rounded-lg text-sm font-medium transition-colors">
          Magasin Parakou
        </button>
      </div>

      <!-- Barre de recherche et actions -->
      <div class="w-full bg-white rounded-xl shadow-sm p-4 mb-4">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="relative flex-1 max-w-md">
            <i class="fa-solid fa-search text-gray-400 absolute left-3 top-3"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              @input="searchProduit" 
              placeholder="Rechercher un produit..." 
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition"
            >
          </div>
          <div class="flex gap-2">
            <button @click="ajouterArticle()" class="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors">
              <i class="fa-solid fa-add text-xs"></i>
              <span class="hidden md:inline">Ajouter</span>
            </button>
            <button @click="toggleFiltre()" class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors">
              <i class="fa-solid fa-filter text-xs"></i>
              <span class="hidden md:inline">Filtrer</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Liste des lots -->
      <div class="bg-white rounded-xl shadow-sm">
        <ul class="divide-y divide-gray-200">
          <li v-for="(lot, lotIndex) in paginatedLots" :key="lot._id" class="p-4 hover:bg-gray-50 transition-colors relative">
            <div @click="toggleLot(lotIndex)" class="flex items-center justify-between cursor-pointer">
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                  <img :src="lot.photos[0] || defaultImage" @error="onImageError($event)" :alt="lot.name" class="w-full h-full object-cover">
                </div>
                <div>
                  <h3 class="font-medium text-gray-900">{{ lot.name }}</h3>
                  <p class="text-xs text-gray-500">{{ lot.produits.length }} produit(s)</p>
                </div>
              </div>
              
              <div class="flex items-center gap-3">
                <span class="px-2 py-1 text-xs font-medium rounded-full" 
                      :class="{'bg-green-100 text-green-800': invalidCount(lot) === 0, 'bg-blue-100 text-blue-800': invalidCount(lot) > 0}">
                  {{ invalidCount(lot) === 0 ? 'Validé' : `En attente (${invalidCount(lot)})` }}
                </span>
                
                <button class="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                  <i :class="activeLotIndex === lotIndex ? 'fa-chevron-up' : 'fa-chevron-down'" class="fas text-gray-600 text-xs"></i>
                </button>
              </div>
            </div>

            <!-- Liste des produits (accordéon) -->
            <ul v-if="activeLotIndex === lotIndex" class="mt-3 space-y-2">
              <li v-for="(produit, produitIndex) in lot.produits" :key="produitIndex" v-show="produit.quantite > 0" class="bg-gray-50 rounded-lg p-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <span class="text-xs font-medium text-gray-500 hidden md:inline">#{{ produitIndex + 1 }}</span>
                    
                    <div>
                      <div class="flex items-center gap-1">
                        <span class="font-medium" :class="{'line-through text-gray-500': produit.prix_reduit !== produit.prix}">
                          {{ produit.prix }} FCFA
                        </span>
                        <span v-if="produit.prix_reduit !== produit.prix" class="font-medium text-orange-600">
                          {{ produit.prix_reduit }} FCFA
                        </span>
                        <span class="text-xs text-gray-500 ml-1">{{ produit.stock }}</span>
                      </div>
                      
                      <div v-if="produit.capacities && produit.colors" class="text-xs text-gray-700">
                        <span v-if="produit.capacities[0]">{{ produit.capacities[0] }}</span>
                        <span v-if="produit.colors[0]"> - {{ produit.colors[0] }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <span class="text-xs font-medium">
                      Qt: {{ produit.quantite }}
                    </span>

                    <span v-if="produit.isPromo" class="px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-700">
                      <i class="fas fa-fire"></i> Promo
                    </span>

                    <span class="px-2 py-1 text-xs font-medium rounded-full"
                          :class="{'bg-green-100 text-green-800': produit.isValide, 'bg-blue-100 text-blue-800': !produit.isValide}">
                      {{ produit.isValide ? 'Validé' : 'En attente' }}
                    </span>
                    
                    <div class="relative">
                      <button @click.stop="toggleBox(produit._id)" class="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                        <i :class="showBoxId === produit._id ? 'fa-times' : 'fa-ellipsis-vertical'" class="fas text-gray-600 text-xs"></i>
                      </button>
                      
                      <!-- Menu d'actions -->
                      <div v-if="showBoxId === produit._id" class="absolute right-0 z-10 mt-1 w-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1">
                        <button @click="ouvrirModification(lot, produit)" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          <i class="fas fa-pen mr-2"></i> Modifier
                        </button>
                        <button v-if="user.isSuper" @click="toggleValide(lot._id, produit._id)" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          <i :class="produit.isValide ? 'fa-times-circle' : 'fa-check-circle'" class="fas mr-2"></i>
                          {{ produit.isValide ? 'Rejeter' : 'Valider' }}
                        </button>
                        <button v-if="user.isSuper" @click="toggleOnline(lot._id, produit._id)" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          <i :class="produit.isOnline ? 'fa-eye-slash' : 'fa-eye'" class="fas mr-2"></i>
                          {{ produit.isOnline ? 'Hors ligne' : 'En ligne' }}
                        </button>
                        <button v-if="user.isSuper" @click="togglePromo(lot._id, produit._id)" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          <i class="fas fa-fire mr-2" :class="produit.isPromo ? 'text-red-500' : ''"></i>
                          {{ produit.isPromo ? 'Retirer la promo' : 'Mettre en promo' }}
                        </button>
                        <button v-if="user.isSuper" @click="confirmDeleteProduit(lot._id, produit._id)" class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                          <i class="fas fa-trash mr-2"></i> Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
            
            <!-- Bouton modifier lot -->
            <button v-if="user.isSuper || invalidCount(lot) !== 0" @click="ouvrirModificationLot(lot)" class="absolute top-[-10px] right-[-10px] w-6 h-6 flex items-center justify-center bg-orange-500 text-white rounded-full shadow-sm hover:bg-orange-600 transition-colors">
              <i class="fas fa-pen text-xs"></i>
            </button>
          </li>
          
          <!-- Aucun résultat -->
          <li v-if="paginatedLots.length === 0 && !isLoading1" class="p-8 text-center text-gray-500">
            Aucun produit disponible
          </li>
          
          <!-- Chargement -->
          <li v-if="isLoading1" class="p-8 w-full flex items-center justify-center text-center">
            <div class="spinner22"></div>
          </li>
        </ul>
        
        <!-- Pagination -->
        <div class="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="flex-1 flex flex-wrap justify-center items-center md:justify-between">
            <div>
              <p class="text-sm text-gray-700 pb-[15px] md:pb-0">
                Affichage de <span class="font-medium">{{ paginationInfo.start }}</span>
                à <span class="font-medium">{{ paginationInfo.end }}</span>
                sur <span class="font-medium">{{ paginationInfo.total }}</span> résultats
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button 
                  @click="goToPage(currentPage - 1)" 
                  :disabled="currentPage === 1"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span class="sr-only">Précédent</span>
                  <i class="fas fa-chevron-left"></i>
                </button>

                <template v-if="totalPages > 1">
                  <button 
                    v-for="page in visiblePages" 
                    :key="page"
                    @click="goToPage(page)"
                    :class="{
                      'z-10 bg-orange-50 border-orange-500 text-orange-600': currentPage === page,
                      'bg-white border-gray-300 text-gray-500 hover:bg-gray-50': currentPage !== page
                    }"
                    class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                  >
                    {{ page }}
                  </button>
                </template>

                <button 
                  @click="goToPage(currentPage + 1)" 
                  :disabled="currentPage === totalPages"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span class="sr-only">Suivant</span>
                  <i class="fas fa-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Modifier Article -->
      <div v-if="modifieArticle && ModifieuseLot" class="fixed inset-0 lg:left-[280px] z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="fermerModification">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Modifier un article</h3>
                <button @click="fermerModification" class="text-gray-400 hover:text-gray-500">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <form @submit.prevent="submitModification" class="space-y-4">
                <!-- Capacité -->
                <div v-if="ModifieLotCategorie == 'iOS' || ModifieLotCategorie == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Capacité du téléphone</label>
                  <div class="flex gap-2">
                    <input v-model="ModifienewCapacity" type="text" class="flex-1 p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <button @click.prevent="ModifieaddCapacity" type="button" class="px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600">
                      Ajouter
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="(capacity, index) in Modifiecapacities" :key="index" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {{ capacity }}
                      <button @click.prevent="ModifieremoveCapacity(index)" class="ml-1.5 inline-flex text-orange-400 hover:text-orange-600">
                        <i class="fas fa-times"></i>
                      </button>
                    </span>
                  </div>
                </div>
                
                <!-- Couleur -->
                <div v-if="ModifieLotCategorie == 'iOS' || ModifieLotCategorie == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Couleur du téléphone</label>
                  <div class="flex gap-2">
                    <input v-model="ModifienewColor" type="text" class="flex-1 p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <button @click.prevent="ModifieaddColor" type="button" class="px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600">
                      Ajouter
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="(color, index) in Modifiecolors" :key="index" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {{ color }}
                      <button @click.prevent="ModifieremoveColor(index)" class="ml-1.5 inline-flex text-orange-400 hover:text-orange-600">
                        <i class="fas fa-times"></i>
                      </button>
                    </span>
                  </div>
                </div>
                
                <!-- IMEI -->
                <div v-if="ModifieLotCategorie == 'iOS' || ModifieLotCategorie == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">IMEI 1 <span class="text-red-500">*</span></label>
                  <input v-model="Modifieimei1" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <div v-if="ModifieLotCategorie == 'iOS' || ModifieLotCategorie == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">IMEI 2 <span class="text-red-500">*</span></label>
                  <input v-model="Modifieimei2" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Stock -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Stock <span class="text-red-500">*</span></label>
                  <select v-model="Modifiestock" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <option v-if="user.isSuper" value="Stock Parakou">Stock Parakou</option>
                    <option v-if="user.isSuper" value="Stock Calavi">Stock Calavi</option>
                    <option v-if="user.isSuper" value="Magasin Parakou">Magasin Parakou</option>
                    <option v-if="user.isSuper" value="Magasin Calavi">Magasin Calavi</option>
                    <option v-else-if="user.boutique == 'Parakou'" value="Stock Parakou">Stock Parakou</option>
                    <option v-else-if="user.boutique == 'Calavi'" value="Stock Calavi">Stock Calavi</option>
                  </select>
                </div>
                
                <!-- Quantité -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Quantité <span class="text-red-500">*</span></label>
                  <input v-model="Modifiequantite" type="number" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Prix -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Prix <span class="text-red-500">*</span></label>
                  <div class="relative">
                    <input v-model.number="Modifieprix" type="number" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <span class="absolute right-3 top-2.5 text-gray-500">FCFA</span>
                  </div>
                </div>
                
                <!-- Réduction -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Réduction</label>
                  <input v-model.number="Modifiereduction" type="number" class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Calculs réduction -->
                <div v-if="Modifieprix && Modifiereduction" class="grid grid-cols-2 gap-4">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Pourcentage</label>
                    <div class="p-2 bg-gray-100 rounded-md">
                      {{ ModifiepourcentageReduction }}%
                    </div>
                  </div>
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Prix final</label>
                    <div class="p-2 bg-gray-100 rounded-md">
                      {{ ModifiemontantRestant }} FCFA
                    </div>
                  </div>
                </div>
                
                <!-- Bouton soumission -->
                <div class="pt-4">
                  <button type="submit" class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                    <span v-if="!isLoading">Modifier</span>
                    <div v-if="isLoading" class="spinner"></div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Modifier Lot -->
      <div v-if="modifieArticle && ModifiecreerLot" class="fixed inset-0 lg:left-[280px] z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="fermerModification">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="flex justify-between items-center mb-4">
                <div class="flex items-center">
                  <button v-if="user.isSuper" @click="confirmDeleteLot(ModifielotId)" class="w-6 h-6 flex items-center justify-center bg-red-500 mr-[10px] text-white rounded-full shadow-sm hover:bg-red-600 transition-colors">
                    <i class="fas fa-trash text-xs"></i>
                  </button>
                  <h3 class="text-lg leading-6 font-medium text-gray-900">Modifier un lot</h3>
                </div>
                <button @click="fermerModification" class="text-gray-400 hover:text-gray-500">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <form @submit.prevent="ModifieLot" class="space-y-4">
                <!-- Images -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Images <span class="text-red-500">*</span></label>
                  <div v-if="!selectedImages.length" @click="triggerFileInput" class="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition">
                    <i class="fas fa-camera text-gray-400 text-3xl mb-2"></i>
                    <p class="text-sm text-gray-500">Ajouter des images</p>
                  </div>
                  <div v-else class="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg relative">
                    <img :src="selectedImages[0]" class="w-full h-full object-cover rounded-lg">
                    <button @click="removeImage(0)" class="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <i class="fas fa-times text-xs"></i>
                    </button>
                  </div>
                  <input type="file" ref="fileInput" @change="handleFileChange" multiple accept="image/*" class="hidden">
                  
                  <div class="flex gap-2 mt-2 overflow-x-auto py-2">
                    <div v-for="(image, index) in selectedImages" :key="index" class="relative w-16 h-16 flex-shrink-0">
                      <img :src="image" class="w-full h-full object-cover rounded-md border border-gray-200">
                      <button @click="removeImage(index)" class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center">
                        <i class="fas fa-times text-2xs"></i>
                      </button>
                    </div>
                    <div @click="triggerFileInput" class="w-16 h-16 flex-shrink-0 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-orange-500">
                      <i class="fas fa-plus text-gray-400"></i>
                    </div>
                  </div>
                </div>
                
                <!-- Catégorie -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Catégorie <span class="text-red-500">*</span></label>
                  <div class="grid grid-cols-3 gap-3">
                    <div v-for="(category, index) in categories" :key="index" 
                         :class="{'ring-2 ring-orange-500': ModifieLotCategorie === category.name}"
                         class="relative p-3 border border-gray-200 rounded-lg cursor-pointer">
                      <input type="radio" :id="'mod-category-' + index" name="mod-categorie" 
                             :value="category.name" v-model="ModifieLotCategorie" 
                             class="absolute opacity-0 w-0 h-0">
                      <label :for="'mod-category-' + index" class="flex flex-col items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" class="mb-1">
                          <path fill="currentColor" :d="category.icone"/>
                        </svg>
                        <span class="text-sm font-medium text-gray-700">{{ category.name }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <!-- Nom du lot -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Nom du lot <span class="text-red-500">*</span></label>
                  <input v-model="ModifienomLot" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Description -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Description</label>
                  <textarea v-model="Modifiedescription" rows="3" class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"></textarea>
                </div>
                
                
                <!-- Bouton soumission -->
                <div class="pt-4">
                  <button type="submit" class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                    <span v-if="!isLoading">Modifier</span>
                    <div v-if="isLoading" class="spinner"></div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Ajouter Article -->
      <div v-if="ajouteArticle" class="fixed inset-0 lg:left-[280px] z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="fermerAjout">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Ajouter un article</h3>
                <button @click="fermerAjout" class="text-gray-400 hover:text-gray-500">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <div class="flex border-b mb-4">
                <button @click="toggleuseLot" :class="{'border-b-2 border-orange-500 text-orange-600': useLot, 'text-gray-500 hover:text-gray-700': !useLot}" class="px-4 py-2 text-sm font-medium">
                  Ajouter à un lot
                </button>
                <button @click="togglecreerLot" :class="{'border-b-2 border-orange-500 text-orange-600': creerLot, 'text-gray-500 hover:text-gray-700': !creerLot}" class="px-4 py-2 text-sm font-medium">
                  Créer un lot
                </button>
              </div>
              
              <!-- Formulaire Ajouter à un lot -->
              <form v-if="useLot" @submit.prevent="submitForm" class="space-y-4">
                <!-- Sélection du lot -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Nom du lot <span class="text-red-500">*</span></label>
                  <input v-model="nomuseLot" @input="suggestions('nomuseLot')" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                  <ul v-if="suggestionsLot.length" class="mt-1 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                    <li v-for="(lot, index) in suggestionsLot" :key="index" @click="selectLot(lot)" class="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                      {{ lot.name }}
                    </li>
                  </ul>
                </div>
                
                <!-- Capacité -->
                <div v-if="selectedLotCategori == 'iOS' || selectedLotCategori == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Capacité du téléphone</label>
                  <div class="flex gap-2">
                    <input v-model="newCapacity" type="text" class="flex-1 p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <button @click.prevent="addCapacity" type="button" class="px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600">
                      Ajouter
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="(capacity, index) in capacities" :key="index" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {{ capacity }}
                      <button @click.prevent="removeCapacity(index)" class="ml-1.5 inline-flex text-orange-400 hover:text-orange-600">
                        <i class="fas fa-times"></i>
                      </button>
                    </span>
                  </div>
                </div>
                
                <!-- Couleur -->
                <div v-if="selectedLotCategori == 'iOS' || selectedLotCategori == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Couleur du téléphone</label>
                  <div class="flex gap-2">
                    <input v-model="newColor" type="text" class="flex-1 p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <button @click.prevent="addColor" type="button" class="px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600">
                      Ajouter
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="(color, index) in colors" :key="index" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {{ color }}
                      <button @click.prevent="removeColor(index)" class="ml-1.5 inline-flex text-orange-400 hover:text-orange-600">
                        <i class="fas fa-times"></i>
                      </button>
                    </span>
                  </div>
                </div>
                
                <!-- IMEI -->
                <div v-if="selectedLotCategori == 'iOS' || selectedLotCategori == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">IMEI 1 <span class="text-red-500">*</span></label>
                  <input v-model="imei1" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <div v-if="selectedLotCategori == 'iOS' || selectedLotCategori == 'Android'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">IMEI 2 <span class="text-red-500">*</span></label>
                  <input v-model="imei2" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Stock -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Stock <span class="text-red-500">*</span></label>
                  <select v-model="stock" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <option v-if="user.isSuper" value="Stock Parakou">Stock Parakou</option>
                    <option v-if="user.isSuper" value="Stock Calavi">Stock Calavi</option>
                    <option v-if="user.isSuper" value="Magasin Parakou">Magasin Parakou</option>
                    <option v-if="user.isSuper" value="Magasin Calavi">Magasin Calavi</option>
                    <option v-else-if="user.boutique == 'Parakou'" value="Stock Parakou">Stock Parakou</option>
                    <option v-else-if="user.boutique == 'Calavi'" value="Stock Calavi">Stock Calavi</option>
                  </select>
                </div>
                
                <!-- Quantité -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Quantité <span class="text-red-500">*</span></label>
                  <input v-model="quantite" type="number" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Prix -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Prix <span class="text-red-500">*</span></label>
                  <div class="relative">
                    <input v-model.number="prix" type="number" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                    <span class="absolute right-3 top-2.5 text-gray-500">FCFA</span>
                  </div>
                </div>
                
                <!-- Réduction -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Réduction</label>
                  <input v-model.number="reduction" type="number" class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Calculs réduction -->
                <div v-if="prix && reduction" class="grid grid-cols-2 gap-4">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Pourcentage</label>
                    <div class="p-2 bg-gray-100 rounded-md">
                      {{ pourcentageReduction }}%
                    </div>
                  </div>
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Prix final</label>
                    <div class="p-2 bg-gray-100 rounded-md">
                      {{ montantRestant }} FCFA
                    </div>
                  </div>
                </div>
                
                <!-- Bouton soumission -->
                <div class="pt-4">
                  <button type="submit" class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                    <span v-if="!isLoading">Ajouter</span>
                    <div v-if="isLoading" class="spinner"></div>
                  </button>
                </div>
              </form>
              
              <!-- Formulaire Créer un lot -->
              <form v-if="creerLot" @submit.prevent="createLot" class="space-y-4">
                <!-- Images -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Images <span class="text-red-500">*</span></label>
                  <div v-if="!selectedImages.length" @click="triggerFileInput" class="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition">
                    <i class="fas fa-camera text-gray-400 text-3xl mb-2"></i>
                    <p class="text-sm text-gray-500">Ajouter des images</p>
                  </div>
                  <div v-else class="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg relative">
                    <img :src="selectedImages[0]" class="w-full h-full object-cover rounded-lg">
                    <button @click="removeImage(0)" class="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <i class="fas fa-times text-xs"></i>
                    </button>
                  </div>
                  <input type="file" ref="fileInput" @change="handleFileChange" multiple accept="image/*" required class="hidden">
                  
                  <div class="flex gap-2 mt-2 overflow-x-auto py-2">
                    <div v-for="(image, index) in selectedImages" :key="index" class="relative w-16 h-16 flex-shrink-0">
                      <img :src="image" class="w-full h-full object-cover rounded-md border border-gray-200">
                      <button @click="removeImage(index)" class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center">
                        <i class="fas fa-times text-2xs"></i>
                      </button>
                    </div>
                    <div @click="triggerFileInput" class="w-16 h-16 flex-shrink-0 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-orange-500">
                      <i class="fas fa-plus text-gray-400"></i>
                    </div>
                  </div>
                </div>
                
                <!-- Catégorie -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Catégorie <span class="text-red-500">*</span></label>
                  <div class="grid grid-cols-3 gap-3">
                    <div v-for="(category, index) in categories" :key="index" 
                         :class="{'ring-2 ring-orange-500': categori === category.name}"
                         class="relative p-3 border border-gray-200 rounded-lg cursor-pointer">
                      <input type="radio" :id="'category-' + index" name="categorie" 
                             :value="category.name" v-model="categori" 
                             class="absolute opacity-0 w-0 h-0">
                      <label :for="'category-' + index" class="flex flex-col items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" class="mb-1">
                          <path fill="currentColor" :d="category.icone"/>
                        </svg>
                        <span class="text-sm font-medium text-gray-700">{{ category.name }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <!-- Nom du lot -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Nom du lot <span class="text-red-500">*</span></label>
                  <input v-model="nomLot" type="text" required class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500">
                </div>
                
                <!-- Description -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Description</label>
                  <textarea v-model="description" rows="3" class="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"></textarea>
                </div>
              
                
                <!-- Bouton soumission -->
                <div class="pt-4">
                  <button type="submit" class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                    <span v-if="!isLoading">Créer</span>
                    <div v-if="isLoading" class="spinner"></div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import HeaderComponent from "../components/HeaderComponent.vue"
import axios from 'axios'
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/store/auth';

const authStore = useAuthStore();
const user = computed(() => authStore.user);
const isLoading = ref(false)
const currentPage = ref(1)

// Modals
const modifieArticle = ref(false)
const ajouteArticle = ref(false)
const ModifieuseLot = ref(true)
const ModifiecreerLot = ref(false)
const useLot = ref(true)
const creerLot = ref(false)

// Données
const lots = ref([])
const filteredLot = ref([])
const defaultImage = '/img/default.png'
const errorMessage = ref('')
const successMessage = ref('')
const isLoading1 = ref(false)
const searchQuery = ref('')
const selectedStock = ref('')
const isfiltre = ref(false)

// Pagination
const paginationInfo = computed(() => {
  if (lots.value.length === 0) return { start: 0, end: 0, total: 0 };
  
  const start = (currentPage.value - 1) * 20 + 1;
  const end = Math.min(currentPage.value * 20, responseData.value?.pagination?.totalLots || 0);
  const total = responseData.value?.pagination?.totalLots || 0;
  
  return { start, end, total };
});

// Modifiez visiblePages pour utiliser totalPages du backend
const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  let startPage = Math.max(1, currentPage.value - 2);
  let endPage = Math.min(startPage + 4, total);
  
  if (endPage - startPage < 4 && startPage > 1) {
    startPage = Math.max(1, endPage - 4);
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }
  
  return pages;
});

const paginatedLots = computed(() => {
  // Le backend gère déjà la pagination, donc on utilise directement filteredLot
  return filteredLot.value.map(lot => {
    // Tri des produits pour mettre les non validés en premier
    const sortedProduits = [...lot.produits].sort((a, b) => 
      (a.isValide === b.isValide) ? 0 : a.isValide ? 1 : -1
    );
    return { ...lot, produits: sortedProduits };
  });
});

const totalPages = computed(() => {
  return responseData.value?.pagination?.totalPages || 1;
});

const itemsPerPage = computed(() => {
  return 20; // Fixé à 20 comme dans votre backend
});

// Fonctions
function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchLots(); // Recharge les données depuis le serveur
  }
}

function toggleFiltre() {
  isfiltre.value = !isfiltre.value
}

function onImageError(event) {
  event.target.src = defaultImage
}

const activeLotIndex = ref(null)
function toggleLot(index) {
  activeLotIndex.value = activeLotIndex.value === index ? null : index
}

const showBoxId = ref(null)
function toggleBox(id) {
  showBoxId.value = showBoxId.value === id ? null : id
}

const invalidCount = (lot) => {
  return lot.produits.filter(produit => !produit.isValide).length
}

// Gestion des stocks
function filterByStock(stock) {
  selectedStock.value = stock
  currentPage.value = 1;
  fetchLots();
}

function clearStockFilter() {
  selectedStock.value = ''
  currentPage.value = 1;
  fetchLots();
}

// Fetch des lots
const responseData = ref(null); // Ajoutez cette ligne pour stocker la réponse complète

const fetchLots = async () => {
  isLoading1.value = true;
  try {
    const params = new URLSearchParams({
      page: currentPage.value,
      ...(searchQuery.value && { search: searchQuery.value }),
      ...(selectedStock.value && { stock: selectedStock.value })
    });

    const paramsSup = {
      boutique: user.value.boutique,
      poste: user.value.poste,
      isSuper: user.value.isSuper
    };

    const response = await axios.get(`/api/lots?${params.toString()}`, { params: paramsSup });
    responseData.value = response.data;
    lots.value = response.data.lots;
    filteredLot.value = response.data.lots;
    
  } catch (error) {
    errorMessage.value = 'Erreur lors du chargement des produits';
    hideErrorMessageAfterDelay();
  } finally {
    isLoading1.value = false;
  }
};


// Recherche
const searchProduit = () => {
  currentPage.value = 1
  fetchLots()
}

// Gestion des modals
const toggleuseLot = () => {
  creerLot.value = false
  useLot.value = true
}

const togglecreerLot = () => {
  useLot.value = false
  creerLot.value = true
}

const fermerModification = () => {
  modifieArticle.value = false
}

const fermerAjout = () => {
  ajouteArticle.value = false
}

const ajouterArticle = () => {
  ajouteArticle.value = true
  modifieArticle.value = false
}

// Gestion des images
const selectedImages = ref([])
const fileInput = ref(null)

const triggerFileInput = () => {
  fileInput.value.click()
}

const handleFileChange = (event) => {
  const files = event.target.files
  for (let i = 0; i < files.length; i++) {
    const reader = new FileReader()
    reader.onload = (e) => {
      selectedImages.value.push(e.target.result)
    }
    reader.readAsDataURL(files[i])
  }
}

const removeImage = (index) => {
  selectedImages.value.splice(index, 1)
}

// Gestion des capacités et couleurs
const newCapacity = ref('')
const capacities = ref([])
const newColor = ref('')
const colors = ref([])

function addCapacity() {
  if (newCapacity.value) {
    capacities.value.push(newCapacity.value)
    newCapacity.value = ''
  }
}

function removeCapacity(index) {
  capacities.value.splice(index, 1)
}

function addColor() {
  if (newColor.value) {
    colors.value.push(newColor.value)
    newColor.value = ''
  }
}

function removeColor(index) {
  colors.value.splice(index, 1)
}

const ModifienewCapacity = ref('')
const Modifiecapacities = ref([])
const ModifienewColor = ref('')
const Modifiecolors = ref([])

function ModifieaddCapacity() {
  if (ModifienewCapacity.value) {
    Modifiecapacities.value.push(ModifienewCapacity.value)
    ModifienewCapacity.value = ''
  }
}

function ModifieremoveCapacity(index) {
  Modifiecapacities.value.splice(index, 1)
}

function ModifieaddColor() {
  if (ModifienewColor.value) {
    Modifiecolors.value.push(ModifienewColor.value)
    ModifienewColor.value = ''
  }
}

function ModifieremoveColor(index) {
  Modifiecolors.value.splice(index, 1)
}

// Formulaires
const nomuseLot = ref('')
const suggestionsLot = ref([])
const selectedLotId = ref(null)
const selectedLotCategori = ref('')

const suggestions = (inputName) => {
  const input = nomuseLot.value
  
  const suggestions = lots.value.filter(p => 
    p.name.toLowerCase().includes(input.toLowerCase()) && 
    categories.value.some(categorie => categorie.name === p.categorie) &&
    !p.name.toLowerCase().includes('occasion')
  )
  
  if (inputName === 'nomuseLot') {
    suggestionsLot.value = suggestions
  }
}

const selectLot = (lot) => {
  nomuseLot.value = lot.name
  selectedLotId.value = lot._id
  selectedLotCategori.value = lot.categorie 
  suggestionsLot.value = []
}

const prix = ref(null)
const reduction = ref(null)
const Modifiereduction = ref(null)

const pourcentageReduction = computed(() => {
  if (prix.value && reduction.value) {
    return ((reduction.value / prix.value) * 100).toFixed(2)
  }
  return 0
})

const ModifiepourcentageReduction = computed(() => {
  if (Modifieprix.value && Modifiereduction.value) {
    return ((Modifiereduction.value / Modifieprix.value) * 100).toFixed(2)
  }
  return 0
})

const montantRestant = computed(() => {
  if (prix.value && reduction.value) {
    return (prix.value - reduction.value).toFixed(2)
  }
  return prix.value
})

const ModifiemontantRestant = computed(() => {
  if (Modifieprix.value && Modifiereduction.value) {
    return (Modifieprix.value - Modifiereduction.value).toFixed(2)
  }
  return Modifieprix.value
})

// Données formulaire
const nomLot = ref('')
const categori = ref('')
const quantite = ref(1)
const description = ref('')
const stock = ref('')
const prix_reduit = ref(0)
const seuil = ref(null)
const imei1 = ref('')
const imei2 = ref('')
const etat = ref('')

const ModifienomLot = ref('')
const ModifieLotCategorie = ref('')
const Modifiequantite = ref('')
const Modifiedescription = ref('')
const Modifiestock = ref('')
const Modifieprix_reduit = ref(0)
const Modifieprix = ref(0)
const Modifieseuil = ref(0)
const Modifieimei1 = ref('')
const Modifieimei2 = ref('')
const Modifieetat = ref('')
const ModifielotId = ref('')
const ModifieId = ref('')

// Soumission des formulaires
const submitForm = async () => {
  if (montantRestant.value != 0) {
    prix_reduit.value = Number(montantRestant.value)
  }
  if (montantRestant.value == 0) {
    prix_reduit.value = Number(prix.value)
  }
  
  try {
    isLoading.value = true
    const response = await axios.put(`/api/lots/${selectedLotId.value}/produit`, {
      quantite: quantite.value,
      prix: prix.value,
      stock: stock.value,
      imei1: imei1.value,
      imei2: imei2.value,
      prix_reduit: prix_reduit.value,
      capacities: capacities.value,
      colors: colors.value
    })
    
    // Réinitialisation
    quantite.value = ''
    stock.value = ''
    prix.value = ''
    prix_reduit.value = ''
    ajouteArticle.value = false
    imei1.value = ''
    imei2.value = ''
    capacities.value = []
    colors.value = []
    
    fetchLots()
    successMessage.value = "Le produit a été ajouté avec succès"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors de l\'ajout du produit'
    hideErrorMessageAfterDelay()
  } finally {
    isLoading.value = false
  }
}

const createLot = async () => {
  const formData = new FormData()
  formData.append('name', nomLot.value)
  formData.append('categorie', categori.value)
  formData.append('description', description.value)
  formData.append('seuil', seuil.value)

  for (const file of fileInput.value.files) {
    formData.append('photos', file)
  }
  
  isLoading.value = true
  try {
    const response = await axios.post('/api/lots', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    // Réinitialisation
    nomLot.value = ''
    categori.value = ''
    description.value = ''
    seuil.value = ''
    selectedImages.value = []
    
    togglecreerLot()
    fetchLots()
    successMessage.value = "Lot créé avec succès"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors de la création du lot'
    hideErrorMessageAfterDelay()
  } finally {
    isLoading.value = false
  }
}

const submitModification = async () => {
  if (ModifiemontantRestant.value != 0) {
    Modifieprix_reduit.value = Number(ModifiemontantRestant.value)
  }
  if (ModifiemontantRestant.value == 0) {
    Modifieprix_reduit.value = Modifieprix.value
  }

  isLoading.value = true
  try {
    await axios.put(`/api/lots/${ModifielotId.value}/produit/${ModifieId.value}/`, {
      quantite: Modifiequantite.value,
      prix: Modifieprix.value,
      stock: Modifiestock.value,
      seuil: Modifieseuil.value,
      etat: Modifieetat.value,
      imei1: Modifieimei1.value,
      imei2: Modifieimei2.value,
      prix_reduit: Modifieprix_reduit.value,
      capacities: Modifiecapacities.value,
      colors: Modifiecolors.value
    })
    
    // Réinitialisation
    Modifiequantite.value = ''
    Modifiestock.value = ''
    Modifieprix.value = 0
    Modifieprix_reduit.value = 0
    Modifieseuil.value = 0
    Modifieetat.value = ''
    Modifieimei1.value = ''
    Modifieimei2.value = ''
    Modifiecapacities.value = []
    Modifiecolors.value = []
    
    fetchLots()
    fermerModification()
    successMessage.value = "Produit modifié avec succès"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors de la modification du produit'
    hideErrorMessageAfterDelay()
  } finally {
    isLoading.value = false
  }
}

const ModifieLot = async () => {
  const formData = new FormData()
  formData.append('name', ModifienomLot.value)
  formData.append('categorie', ModifieLotCategorie.value)
  formData.append('description', Modifiedescription.value)
  formData.append('seuil', Modifieseuil.value)

  if (fileInput.value.files.length > 0) {
    for (const file of fileInput.value.files) {
      formData.append('photos', file)
    }
  }

  isLoading.value = true
  try {
    const response = await axios.put(`/api/lots/${ModifielotId.value}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    // Réinitialisation
    ModifienomLot.value = ''
    ModifieLotCategorie.value = ''
    Modifiedescription.value = ''
    Modifieseuil.value = ''
    selectedImages.value = []
    
    fetchLots()
    modifieArticle.value = false
    ModifiecreerLot.value = false
    successMessage.value = "Lot modifié avec succès"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors de la modification du lot'
    hideErrorMessageAfterDelay()
  } finally {
    isLoading.value = false
  }
}

// Actions sur les produits
const toggleValide = async (Lotid, produitId) => {
  try {
    await axios.put(`/api/lots/${Lotid}/produits/${produitId}/toggle-isValide`)
    showBoxId.value = null
    fetchLots()
    successMessage.value = "Statut du produit mis à jour"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors du changement de statut'
    hideErrorMessageAfterDelay()
  }
}

const toggleOnline = async (Lotid, produitId) => {
  try {
    await axios.put(`/api/lots/${Lotid}/produits/${produitId}/toggle-isOnline`)
    showBoxId.value = null
    fetchLots()
    successMessage.value = "Visibilité du produit mise à jour"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors du changement de visibilité'
    hideErrorMessageAfterDelay()
  }
}

const togglePromo = async (Lotid, produitId) => {
  try {
    await axios.put(`/api/lots/${Lotid}/produits/${produitId}/toggle-isPromo`)
    showBoxId.value = null
    fetchLots()
    successMessage.value = "Statut promo mis à jour"
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors du changement de promo'
    hideErrorMessageAfterDelay()
  }
}

const ouvrirModification = (lot, produit) => {
  modifieArticle.value = true
  ajouteArticle.value = false
  ModifieuseLot.value = true
  ModifiecreerLot.value = false
  
  Modifiequantite.value = produit.quantite
  Modifiestock.value = produit.stock
  Modifieprix.value = produit.prix
  ModifielotId.value = lot._id
  ModifieLotCategorie.value = lot.categorie
  ModifieId.value = produit._id
  Modifieseuil.value = produit.seuil
  Modifieetat.value = produit.etat
  Modifieimei1.value = produit.imei1
  Modifieimei2.value = produit.imei2
  Modifiecapacities.value = [...produit.capacities]
  Modifiecolors.value = [...produit.colors]
  Modifieprix_reduit.value = produit.prix_reduit
}

const ouvrirModificationLot = (lot) => {
  modifieArticle.value = true
  ajouteArticle.value = false
  ModifiecreerLot.value = true
  ModifieuseLot.value = false
  
  ModifielotId.value = lot._id
  ModifieLotCategorie.value = lot.categorie
  ModifienomLot.value = lot.name
  Modifiedescription.value = lot.description
  selectedImages.value = [...lot.photos]
  Modifieseuil.value = lot.seuil
}

// Messages
const hideSuccessMessageAfterDelay = () => {
  setTimeout(() => {
    successMessage.value = ''
  }, 5000)
}

const hideErrorMessageAfterDelay = () => {
  setTimeout(() => {
    errorMessage.value = ''
  }, 10000)
}

// Catégories
const categories = ref([])

const setCategories = (poste) => {
  const allCategories = {
    'Manager': [
      {
          'name': 'iOS',
          'icone': 'M17.05 20.28c-.98.95-2.05.8-3.08.35c-1.09-.46-2.09-.48-3.24 0c-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8c1.18-.24 2.31-.93 3.57-.84c1.51.12 2.65.72 3.4 1.8c-3.12 1.87-2.38 5.98.48 7.13c-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25c.29 2.58-2.34 4.5-3.74 4.25', 
      }, 
      {
          'name': 'Android',
          'icone': 'm19.817 10.28l.046 2.694h-.023l-.78-2.693h-1.283v3.392h.848l-.046-2.785h.023l.836 2.785h1.227v-3.392zm-16.15 0l-.641 3.428h.928l.47-3.118h.023l.459 3.118h.916l-.63-3.427zm5.181 0l-.424 2.614h-.023l-.424-2.613H6.58l-.069 3.427h.86l.023-3.083h.011l.573 3.083h.871l.573-3.083h.023l.023 3.083h.86l-.08-3.427zm-7.266 2.454a.5.5 0 0 1 .011.252c-.023.114-.103.229-.332.229c-.218 0-.344-.126-.344-.31v-.332H0v.264c0 .768.607.997 1.25.997c.618 0 1.134-.218 1.214-.78c.046-.298.012-.492 0-.561c-.16-.722-1.467-.929-1.559-1.33a.5.5 0 0 1 0-.183c.023-.115.104-.23.31-.23s.32.127.32.31v.206h.86v-.24c0-.745-.676-.86-1.157-.86c-.608 0-1.112.206-1.204.757a1.04 1.04 0 0 0 .012.458c.137.71 1.364.917 1.536 1.352m11.152 0c.034.08.022.184.011.253c-.023.114-.103.229-.332.229c-.218 0-.344-.126-.344-.31v-.332h-.917v.264c0 .756.596.985 1.238.985c.619 0 1.123-.206 1.203-.779c.046-.298.012-.481 0-.562c-.137-.71-1.433-.928-1.524-1.318a.5.5 0 0 1 0-.183c.023-.115.103-.23.31-.23c.194 0 .32.127.32.31v.206h.848v-.24c0-.745-.665-.86-1.146-.86c-.607 0-1.1.195-1.192.757c-.023.149-.023.286.012.458c.137.71 1.34.905 1.513 1.352m2.888.459c.24 0 .31-.16.332-.252c.012-.035.012-.092.012-.126V10.28h.87v2.464c0 .069 0 .195-.01.23c-.058.641-.562.847-1.193.847c-.63 0-1.134-.206-1.192-.848c0-.034-.011-.16-.011-.229V10.28h.87v2.533c0 .046 0 .091.012.126c0 .091.07.252.31.252m7.152-.034c.252 0 .332-.16.355-.253c.011-.034.011-.091.011-.126v-.493h-.355v-.504H24v.917c0 .069 0 .115-.011.23c-.058.63-.597.847-1.204.847s-1.146-.217-1.203-.848c-.012-.114-.012-.16-.012-.229v-1.444c0-.057.012-.172.012-.23c.08-.641.596-.847 1.203-.847s1.135.206 1.203.848c.012.103.012.229.012.229v.115h-.86v-.195s0-.08-.011-.126c-.012-.08-.08-.252-.344-.252c-.252 0-.32.16-.344.252c-.011.045-.011.103-.011.16v1.57c0 .046 0 .092.011.126c0 .092.092.253.333.253', 
      }, 
      {
          'name': 'Accessoires',
          'icone': 'M1.5 8.5V13a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V8.5M8 8.5v5M1.5 10H8M.5 4L2 .5h10L13.5 4H.5zm4.25 0v1a2 2 0 0 1-2 2h-.28a2 2 0 0 1-2-2V4m8.78 0v1a2 2 0 0 1-2 2h-.5a2 2 0 0 1-2-2V4m8.75 0v1a2 2 0 0 1-2 2h-.25a2 2 0 0 1-2-2V4'
      }, 
    ],
    'Vendeur(se) Accessoires et Téléphone': [
      {
          'name': 'iOS',
          'icone': 'M17.05 20.28c-.98.95-2.05.8-3.08.35c-1.09-.46-2.09-.48-3.24 0c-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8c1.18-.24 2.31-.93 3.57-.84c1.51.12 2.65.72 3.4 1.8c-3.12 1.87-2.38 5.98.48 7.13c-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25c.29 2.58-2.34 4.5-3.74 4.25', 
      }, 
      {
          'name': 'Android',
          'icone': 'm19.817 10.28l.046 2.694h-.023l-.78-2.693h-1.283v3.392h.848l-.046-2.785h.023l.836 2.785h1.227v-3.392zm-16.15 0l-.641 3.428h.928l.47-3.118h.023l.459 3.118h.916l-.63-3.427zm5.181 0l-.424 2.614h-.023l-.424-2.613H6.58l-.069 3.427h.86l.023-3.083h.011l.573 3.083h.871l.573-3.083h.023l.023 3.083h.86l-.08-3.427zm-7.266 2.454a.5.5 0 0 1 .011.252c-.023.114-.103.229-.332.229c-.218 0-.344-.126-.344-.31v-.332H0v.264c0 .768.607.997 1.25.997c.618 0 1.134-.218 1.214-.78c.046-.298.012-.492 0-.561c-.16-.722-1.467-.929-1.559-1.33a.5.5 0 0 1 0-.183c.023-.115.104-.23.31-.23s.32.127.32.31v.206h.86v-.24c0-.745-.676-.86-1.157-.86c-.608 0-1.112.206-1.204.757a1.04 1.04 0 0 0 .012.458c.137.71 1.364.917 1.536 1.352m11.152 0c.034.08.022.184.011.253c-.023.114-.103.229-.332.229c-.218 0-.344-.126-.344-.31v-.332h-.917v.264c0 .756.596.985 1.238.985c.619 0 1.123-.206 1.203-.779c.046-.298.012-.481 0-.562c-.137-.71-1.433-.928-1.524-1.318a.5.5 0 0 1 0-.183c.023-.115.103-.23.31-.23c.194 0 .32.127.32.31v.206h.848v-.24c0-.745-.665-.86-1.146-.86c-.607 0-1.1.195-1.192.757c-.023.149-.023.286.012.458c.137.71 1.34.905 1.513 1.352m2.888.459c.24 0 .31-.16.332-.252c.012-.035.012-.092.012-.126V10.28h.87v2.464c0 .069 0 .195-.01.23c-.058.641-.562.847-1.193.847c-.63 0-1.134-.206-1.192-.848c0-.034-.011-.16-.011-.229V10.28h.87v2.533c0 .046 0 .091.012.126c0 .091.07.252.31.252m7.152-.034c.252 0 .332-.16.355-.253c.011-.034.011-.091.011-.126v-.493h-.355v-.504H24v.917c0 .069 0 .115-.011.23c-.058.63-.597.847-1.204.847s-1.146-.217-1.203-.848c-.012-.114-.012-.16-.012-.229v-1.444c0-.057.012-.172.012-.23c.08-.641.596-.847 1.203-.847s1.135.206 1.203.848c.012.103.012.229.012.229v.115h-.86v-.195s0-.08-.011-.126c-.012-.08-.08-.252-.344-.252c-.252 0-.32.16-.344.252c-.011.045-.011.103-.011.16v1.57c0 .046 0 .092.011.126c0 .092.092.253.333.253', 
      }, 
      {
          'name': 'Accessoires',
          'icone': 'M1.5 8.5V13a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V8.5M8 8.5v5M1.5 10H8M.5 4L2 .5h10L13.5 4H.5zm4.25 0v1a2 2 0 0 1-2 2h-.28a2 2 0 0 1-2-2V4m8.78 0v1a2 2 0 0 1-2 2h-.5a2 2 0 0 1-2-2V4m8.75 0v1a2 2 0 0 1-2 2h-.25a2 2 0 0 1-2-2V4'
      }, 
    ],
    'Vendeur(se) Téléphone': [
    {
          'name': 'iOS',
          'icone': 'M17.05 20.28c-.98.95-2.05.8-3.08.35c-1.09-.46-2.09-.48-3.24 0c-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8c1.18-.24 2.31-.93 3.57-.84c1.51.12 2.65.72 3.4 1.8c-3.12 1.87-2.38 5.98.48 7.13c-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25c.29 2.58-2.34 4.5-3.74 4.25', 
      }, 
      {
          'name': 'Android',
          'icone': 'm19.817 10.28l.046 2.694h-.023l-.78-2.693h-1.283v3.392h.848l-.046-2.785h.023l.836 2.785h1.227v-3.392zm-16.15 0l-.641 3.428h.928l.47-3.118h.023l.459 3.118h.916l-.63-3.427zm5.181 0l-.424 2.614h-.023l-.424-2.613H6.58l-.069 3.427h.86l.023-3.083h.011l.573 3.083h.871l.573-3.083h.023l.023 3.083h.86l-.08-3.427zm-7.266 2.454a.5.5 0 0 1 .011.252c-.023.114-.103.229-.332.229c-.218 0-.344-.126-.344-.31v-.332H0v.264c0 .768.607.997 1.25.997c.618 0 1.134-.218 1.214-.78c.046-.298.012-.492 0-.561c-.16-.722-1.467-.929-1.559-1.33a.5.5 0 0 1 0-.183c.023-.115.104-.23.31-.23s.32.127.32.31v.206h.86v-.24c0-.745-.676-.86-1.157-.86c-.608 0-1.112.206-1.204.757a1.04 1.04 0 0 0 .012.458c.137.71 1.364.917 1.536 1.352m11.152 0c.034.08.022.184.011.253c-.023.114-.103.229-.332.229c-.218 0-.344-.126-.344-.31v-.332h-.917v.264c0 .756.596.985 1.238.985c.619 0 1.123-.206 1.203-.779c.046-.298.012-.481 0-.562c-.137-.71-1.433-.928-1.524-1.318a.5.5 0 0 1 0-.183c.023-.115.103-.23.31-.23c.194 0 .32.127.32.31v.206h.848v-.24c0-.745-.665-.86-1.146-.86c-.607 0-1.1.195-1.192.757c-.023.149-.023.286.012.458c.137.71 1.34.905 1.513 1.352m2.888.459c.24 0 .31-.16.332-.252c.012-.035.012-.092.012-.126V10.28h.87v2.464c0 .069 0 .195-.01.23c-.058.641-.562.847-1.193.847c-.63 0-1.134-.206-1.192-.848c0-.034-.011-.16-.011-.229V10.28h.87v2.533c0 .046 0 .091.012.126c0 .091.07.252.31.252m7.152-.034c.252 0 .332-.16.355-.253c.011-.034.011-.091.011-.126v-.493h-.355v-.504H24v.917c0 .069 0 .115-.011.23c-.058.63-.597.847-1.204.847s-1.146-.217-1.203-.848c-.012-.114-.012-.16-.012-.229v-1.444c0-.057.012-.172.012-.23c.08-.641.596-.847 1.203-.847s1.135.206 1.203.848c.012.103.012.229.012.229v.115h-.86v-.195s0-.08-.011-.126c-.012-.08-.08-.252-.344-.252c-.252 0-.32.16-.344.252c-.011.045-.011.103-.011.16v1.57c0 .046 0 .092.011.126c0 .092.092.253.333.253', 
      }
    ],
    'Vendeur(se) Accessoires': [
      {
          'name': 'Accessoires',
          'icone': 'M1.5 8.5V13a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V8.5M8 8.5v5M1.5 10H8M.5 4L2 .5h10L13.5 4H.5zm4.25 0v1a2 2 0 0 1-2 2h-.28a2 2 0 0 1-2-2V4m8.78 0v1a2 2 0 0 1-2 2h-.5a2 2 0 0 1-2-2V4m8.75 0v1a2 2 0 0 1-2 2h-.25a2 2 0 0 1-2-2V4'
      },
    ]
  };
  categories.value = allCategories[poste] || [];
}

// Confirmation et suppression d'un produit
const confirmDeleteProduit = (lotId, produitId) => {
  if (confirm('Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.')) {
    deleteProduit(lotId, produitId);
  }
}

const deleteProduit = async (lotId, produitId) => {
  try {
    await axios.delete(`/api/lots/${lotId}/produits/${produitId}`);
    showBoxId.value = null;
    fetchLots();
    successMessage.value = "Produit supprimé avec succès";
    hideSuccessMessageAfterDelay();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la suppression du produit';
    hideErrorMessageAfterDelay();
  }
}

// Confirmation et suppression d'un lot
const confirmDeleteLot = (lotId) => {
  if (confirm('Êtes-vous sûr de vouloir supprimer ce lot et tous ses produits ? Cette action est irréversible.')) {
    deleteLot(lotId);
  }
}

const deleteLot = async (lotId) => {
  try {
    await axios.delete(`/api/lots/${lotId}`);
    fetchLots();
    successMessage.value = "Lot supprimé avec succès";
    ModifiecreerLot.value = false
    hideSuccessMessageAfterDelay();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la suppression du lot';
    hideErrorMessageAfterDelay();
  }
}

// Initialisation
onMounted(async () => {
  await fetchLots();
  setCategories(user.value.poste);
  
  // Définir le stock par défaut selon l'utilisateur
  if (!user.value.isSuper) {
    selectedStock.value = user.value.boutique === 'Calavi' 
      ? 'Stock Calavi' 
      : 'Stock Parakou';
  }
});
</script>

<style scoped>
.spinner22 {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: rgb(234, 88, 12);
  animation: spin 1s ease infinite;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  position: relative;
}

.spinner div {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #fff;
  border-radius: 50%;
  animation: spinner 1.2s linear infinite;
}

.spinner div:nth-child(1) {
  animation-delay: 0s;
  top: 8px;
  left: 0;
}

.spinner div:nth-child(2) {
  animation-delay: -0.1s;
  top: 5px;
  left: 5px;
}

.spinner div:nth-child(3) {
  animation-delay: -0.2s;
  top: 0;
  left: 8px;
}

.spinner div:nth-child(4) {
  animation-delay: -0.3s;
  top: -5px;
  left: 5px;
}

.spinner div:nth-child(5) {
  animation-delay: -0.4s;
  top: -8px;
  left: 0;
}

@keyframes spinner {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.5);
    opacity: 0.5;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>